import React, { useEffect, useState } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import Container from '@/components/ui/container';
import Layout from 'src/layout';
import TableSkeleton from '@/components/ui/tableSkeleton';
import { useNavigate } from 'react-router-dom';
import { getPaginatedProducts, getProductCount, deleteProduct } from './../../lib/api/productsApi';
import InputUi from '@/components/ui/inputui';
import { toast } from 'react-toastify';
import {
    RiSearchLine,
    RiRefreshLine,
    RiAddLine,
    RiEditLine,
    RiEyeLine,
    RiDeleteBinLine,
    RiShoppingBag3Line,
    RiCloseLine
} from 'react-icons/ri';

const CustomProductList = () => {
    const navigate = useNavigate();

    const [products, setProducts] = useState([]);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [page, setPage] = useState(1);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [productToDelete, setProductToDelete] = useState(null);

    const [filters, setFilters] = useState({
        name: '',
        productID: '',
        type: 'customproduct',
        categoryID: '',
        categoryName: ''
    });

    const limit = 10;

    const handleChange = (e, name) => {
        setFilters((prev) => ({ ...prev, [name]: e.target.value }));
    };

    const fetchProductCount = async (overrideFilters = filters) => {
        try {
            const { totalItems: count } = await getProductCount(overrideFilters);
            setTotalItems(count || 0);
            setTotalPages(Math.ceil((count || 0) / limit) || 1);
        } catch (error) {
            console.error('Error counting custom products:', error);
            setTotalItems(0);
        }
    };

    const fetchProducts = async (overrideFilters = filters, pageToFetch = page) => {
        try {
            setLoading(true);
            setRefreshing(true);
            const response = await getPaginatedProducts({
                page: pageToFetch,
                limit,
                filters: overrideFilters
            });
            setProducts(response?.data || []);
        } catch (error) {
            console.error('Error fetching custom products:', error);
            setProducts([]);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    const handleSearch = async () => {
        setPage(1);
        await fetchProductCount(filters);
        await fetchProducts(filters, 1);
    };

    const handleRefresh = async () => {
        await fetchProductCount(filters);
        await fetchProducts(filters, page);
    };

    const handleClearFilters = () => {
        const cleared = {
            name: '',
            productID: '',
            type: 'customproduct',
            categoryID: '',
            categoryName: ''
        };
        setFilters(cleared);
        setPage(1);
        fetchProductCount(cleared);
        fetchProducts(cleared, 1);
    };

    const handlePageChange = (newPage) => {
        if (newPage < 1 || newPage > totalPages || loading) return;
        setPage(newPage);
        fetchProducts(filters, newPage);
    };

    const handleDeleteProduct = async (productID) => {
        try {
            setDeleteLoading(true);
            const response = await deleteProduct(productID);

            if (response.success) {
                toast.success('Custom product deleted successfully');
                await fetchProductCount(filters);
                await fetchProducts(filters, page);
            } else {
                toast.error(response.message || 'Failed to delete custom product');
            }
        } catch (error) {
            console.error('Error deleting custom product:', error);
            toast.error('Error deleting custom product');
        } finally {
            setDeleteLoading(false);
            setProductToDelete(null);
        }
    };

    useEffect(() => {
        fetchProductCount(filters);
        fetchProducts(filters, 1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Layout title="Custom Product List" active="admin-custom-products-list">
            <div className="min-h-screen bg-slate-50/50 p-6 space-y-6">
                <Container>
                    {/* Header Section */}
                    <div className="mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                        <div>
                            <h1 className="text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
                                <RiShoppingBag3Line className="text-indigo-600" /> Custom Products
                            </h1>
                            <p className="text-slate-500 text-sm mt-1">
                                Manage custom-made catalog items, customized apparel, and custom variations.
                            </p>
                        </div>
                        <div className="flex items-center gap-3">
                            <Button
                                onClick={handleRefresh}
                                disabled={refreshing}
                                variant="outline"
                                className="flex items-center gap-2 bg-white hover:bg-slate-100 border-slate-200 text-slate-700 shadow-sm"
                            >
                                <RiRefreshLine className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                                Refresh
                            </Button>
                            <Button
                                onClick={() => navigate('/custom-product/add')}
                                className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm font-medium"
                            >
                                <RiAddLine className="w-4 h-4" />
                                Add Custom Product
                            </Button>
                        </div>
                    </div>

                    {/* Search and Filters */}
                    <div className="bg-white rounded-xl p-5 mb-6 border border-slate-200 shadow-sm space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-3">
                            <div className="relative">
                                <RiSearchLine className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                                <InputUi
                                    placeholder="Product Name"
                                    value={filters.name}
                                    datafunction={(e) => handleChange(e, 'name')}
                                    className="pl-9 text-xs"
                                />
                            </div>
                            <div>
                                <InputUi
                                    placeholder="Product ID"
                                    value={filters.productID}
                                    datafunction={(e) => handleChange(e, 'productID')}
                                    className="text-xs"
                                />
                            </div>
                            <div>
                                <InputUi
                                    placeholder="Type (e.g. customproduct)"
                                    value={filters.type}
                                    datafunction={(e) => handleChange(e, 'type')}
                                    className="text-xs"
                                />
                            </div>
                            <div>
                                <InputUi
                                    placeholder="Category ID"
                                    value={filters.categoryID}
                                    datafunction={(e) => handleChange(e, 'categoryID')}
                                    className="text-xs"
                                />
                            </div>
                            <div>
                                <InputUi
                                    placeholder="Category Name"
                                    value={filters.categoryName}
                                    datafunction={(e) => handleChange(e, 'categoryName')}
                                    className="text-xs"
                                />
                            </div>
                        </div>

                        <div className="flex items-center gap-3">
                            <Button
                                onClick={handleSearch}
                                disabled={loading}
                                className="px-5 bg-indigo-600 hover:bg-indigo-700 text-white font-medium shadow-sm"
                            >
                                {loading ? 'Searching...' : 'Search Products'}
                            </Button>
                            <Button
                                onClick={handleClearFilters}
                                variant="outline"
                                className="px-4 text-slate-600 border-slate-200 hover:bg-slate-50"
                            >
                                <RiCloseLine className="w-4 h-4 mr-1.5" />
                                Clear Filters
                            </Button>
                        </div>
                    </div>

                    {/* Main Table */}
                    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                            <h3 className="text-base font-semibold text-slate-800">Custom Products Catalog</h3>
                            <span className="text-xs font-medium text-slate-500 bg-slate-100 px-2.5 py-1 rounded-full">
                                {totalItems} Products
                            </span>
                        </div>

                        <div className="overflow-x-auto">
                            <Table className="w-full">
                                <TableHeader>
                                    <TableRow className="bg-slate-50/80 border-b border-slate-200">
                                        <TableHead className="px-6 py-3.5 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">
                                            Product ID
                                        </TableHead>
                                        <TableHead className="px-6 py-3.5 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">
                                            Product Details
                                        </TableHead>
                                        <TableHead className="px-6 py-3.5 text-center text-xs font-bold text-slate-600 uppercase tracking-wider">
                                            Regular Price
                                        </TableHead>
                                        <TableHead className="px-6 py-3.5 text-center text-xs font-bold text-slate-600 uppercase tracking-wider">
                                            Sale Price
                                        </TableHead>
                                        <TableHead className="px-6 py-3.5 text-center text-xs font-bold text-slate-600 uppercase tracking-wider">
                                            Type
                                        </TableHead>
                                        <TableHead className="px-6 py-3.5 text-center text-xs font-bold text-slate-600 uppercase tracking-wider">
                                            Discount Type
                                        </TableHead>
                                        <TableHead className="px-6 py-3.5 text-center text-xs font-bold text-slate-600 uppercase tracking-wider">
                                            Created On
                                        </TableHead>
                                        <TableHead className="px-6 py-3.5 text-center text-xs font-bold text-slate-600 uppercase tracking-wider">
                                            Actions
                                        </TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody className="divide-y divide-slate-100">
                                    {loading ? (
                                        <TableSkeleton columns={8} rows={5} />
                                    ) : products.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={8} className="px-6 py-12 text-center">
                                                <div className="flex flex-col items-center justify-center">
                                                    <RiShoppingBag3Line className="w-12 h-12 text-slate-300 mb-3" />
                                                    <p className="text-slate-700 font-semibold text-base">No Custom Products Found</p>
                                                    <p className="text-slate-400 text-xs mt-1">Try refining search query or add a new custom product.</p>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        products.map((product, idx) => {
                                            let imgUrl = '';
                                            try {
                                                const images = typeof product.featuredImage === 'string' ? JSON.parse(product.featuredImage || '[]') : (product.featuredImage || []);
                                                imgUrl = Array.isArray(images) ? (images[0]?.imgUrl || images[0] || '') : '';
                                            } catch (e) {
                                                imgUrl = '';
                                            }

                                            return (
                                                <TableRow
                                                    key={product.productID || idx}
                                                    className="hover:bg-slate-50/80 transition-colors"
                                                >
                                                    <TableCell className="px-6 py-4 whitespace-nowrap">
                                                        <span className="text-sm font-mono font-semibold text-slate-800">
                                                            {product.productID || 'N/A'}
                                                        </span>
                                                    </TableCell>
                                                    <TableCell className="px-6 py-4 whitespace-nowrap">
                                                        <div className="flex items-center gap-3">
                                                            {imgUrl ? (
                                                                <img
                                                                    src={imgUrl}
                                                                    alt={product.name || ''}
                                                                    className="h-10 w-10 rounded-lg border border-slate-200 object-cover flex-shrink-0"
                                                                />
                                                            ) : (
                                                                <div className="h-10 w-10 rounded-lg bg-indigo-50 border border-indigo-100 text-indigo-600 flex items-center justify-center font-bold text-xs flex-shrink-0">
                                                                    CUST
                                                                </div>
                                                            )}
                                                            <div className="max-w-[220px] truncate">
                                                                <p className="text-sm font-semibold text-slate-900 truncate">
                                                                    {product.name || 'Unnamed Product'}
                                                                </p>
                                                                {product.categoryName && (
                                                                    <p className="text-xs text-slate-400 truncate">
                                                                        {product.categoryName}
                                                                    </p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </TableCell>
                                                    <TableCell className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium text-slate-700">
                                                        ₹{product.regularPrice || 0}
                                                    </TableCell>
                                                    <TableCell className="px-6 py-4 whitespace-nowrap text-center text-sm font-bold text-indigo-600">
                                                        {product.salePrice ? `₹${product.salePrice}` : '-'}
                                                    </TableCell>
                                                    <TableCell className="px-6 py-4 whitespace-nowrap text-center">
                                                        <span className="bg-indigo-50 text-indigo-700 border border-indigo-200 px-2.5 py-1 rounded-full text-xs font-semibold uppercase">
                                                            {product.type || 'Custom'}
                                                        </span>
                                                    </TableCell>
                                                    <TableCell className="px-6 py-4 whitespace-nowrap text-center text-xs text-slate-600 capitalize">
                                                        {product.discountType || 'None'}
                                                    </TableCell>
                                                    <TableCell className="px-6 py-4 whitespace-nowrap text-center text-xs text-slate-500">
                                                        {product.createdAt ? new Date(product.createdAt).toLocaleDateString() : '-'}
                                                    </TableCell>
                                                    <TableCell className="px-6 py-4 whitespace-nowrap text-center">
                                                        <div className="flex items-center justify-center gap-2">
                                                            <Button
                                                                variant="outline"
                                                                size="sm"
                                                                onClick={() => navigate(`/custom-product/edit/${product.productID}`)}
                                                                className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                                                                title="Edit Custom Product"
                                                            >
                                                                <RiEditLine className="w-3.5 h-3.5" />
                                                                Edit
                                                            </Button>
                                                            <Button
                                                                variant="outline"
                                                                size="sm"
                                                                onClick={() => navigate(`/products/details/${product.productID}`)}
                                                                className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-blue-600 border-blue-200 hover:bg-blue-50"
                                                                title="View Product Details"
                                                            >
                                                                <RiEyeLine className="w-3.5 h-3.5" />
                                                                View
                                                            </Button>
                                                            <Button
                                                                variant="outline"
                                                                size="sm"
                                                                onClick={() => setProductToDelete(product)}
                                                                disabled={deleteLoading}
                                                                className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-rose-600 border-rose-200 hover:bg-rose-50"
                                                                title="Delete Product"
                                                            >
                                                                <RiDeleteBinLine className="w-3.5 h-3.5" />
                                                            </Button>
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })
                                    )}
                                </TableBody>
                            </Table>
                        </div>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between">
                                <span className="text-xs text-slate-500">
                                    Page <strong className="text-slate-800">{page}</strong> of <strong className="text-slate-800">{totalPages}</strong>
                                </span>
                                <div className="flex items-center gap-2">
                                    <Button
                                        onClick={() => handlePageChange(page - 1)}
                                        disabled={page === 1 || loading}
                                        variant="outline"
                                        size="sm"
                                        className="text-xs h-8 px-3 disabled:opacity-50"
                                    >
                                        Previous
                                    </Button>
                                    <Button
                                        onClick={() => handlePageChange(page + 1)}
                                        disabled={page === totalPages || loading}
                                        variant="outline"
                                        size="sm"
                                        className="text-xs h-8 px-3 disabled:opacity-50"
                                    >
                                        Next
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                </Container>
            </div>

            {/* Delete Confirmation Modal */}
            {productToDelete && (
                <div
                    className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
                    onClick={() => setProductToDelete(null)}
                >
                    <div
                        className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 space-y-4 border border-slate-200"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h3 className="text-lg font-bold text-slate-900">
                            Confirm Deletion
                        </h3>
                        <p className="text-slate-600 text-sm">
                            Are you sure you want to delete the custom product <strong className="text-slate-900">"{productToDelete.name}"</strong>?
                            This action cannot be undone.
                        </p>
                        <div className="flex items-center justify-end gap-3 pt-2">
                            <Button
                                variant="outline"
                                onClick={() => setProductToDelete(null)}
                                className="border-slate-200 text-slate-700"
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={() => handleDeleteProduct(productToDelete.productID)}
                                disabled={deleteLoading}
                                className="bg-rose-600 hover:bg-rose-700 text-white font-medium"
                            >
                                {deleteLoading ? 'Deleting...' : 'Delete Product'}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </Layout>
    );
};

export default CustomProductList;